import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommandExecutionRow } from '@displaycms/contracts';

/**
 * P5 W1 §2.4 — Screenshot viewer modal.
 *
 * Opens when a `player.capture_screenshot` command completes (the
 * parent passes the freshly-completed `command_id`). Fetches the
 * persisted `CommandExecutionRow` via `GET
 * /api/v1/admin/players/:id/commands/:command_id` (endpoint added
 * in T3) and reads `row.result` for the JPEG/PNG metadata:
 *
 *   - `bytes_url`    — signed pre-fetch URL, used directly as <img src>
 *   - `width`/`height`
 *   - `size_bytes`
 *   - `captured_at`
 *
 * The image renders at max 70vh × full container width so a portrait
 * 4K capture remains usable inside a desktop modal.
 */

export interface ScreenshotViewerModalProps {
  commandId: string | null;
  playerId: string;
  onClose: () => void;
}

interface ScreenshotMeta {
  screenshot_id: string;
  sha256?: string;
  width: number;
  height: number;
  captured_at: string;
  bytes_url: string;
  size_bytes: number;
}

export function ScreenshotViewerModal({
  commandId,
  playerId,
  onClose,
}: ScreenshotViewerModalProps): JSX.Element | null {
  const { t } = useTranslation(['players']);
  const [meta, setMeta] = useState<ScreenshotMeta | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!commandId) {
      setMeta(null);
      setError(null);
      return;
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const res = await fetch(
          `/api/v1/admin/players/${playerId}/commands/${commandId}`,
          {
            credentials: 'include',
            headers: { 'X-Requested-With': 'displaycms-admin' },
          },
        );
        if (!res.ok) {
          throw new Error(`screenshot_fetch_failed_${res.status}`);
        }
        const row = (await res.json()) as CommandExecutionRow;
        if (cancelled) return;
        const result = row.result as Record<string, unknown> | null;
        if (!result || typeof result !== 'object') {
          throw new Error('screenshot_result_missing');
        }
        setMeta(result as unknown as ScreenshotMeta);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commandId, playerId]);

  if (!commandId) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="screenshot-modal-title"
      data-testid="screenshot-viewer-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-md border border-border bg-card p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-center justify-between">
          <h3 id="screenshot-modal-title" className="font-semibold">
            {t('players:cmd.screenshot_modal_title')}
          </h3>
          <button
            type="button"
            data-testid="screenshot-close"
            onClick={onClose}
            className="rounded-md border border-input bg-background px-2 py-1 text-sm"
          >
            ✕
          </button>
        </header>
        {error && (
          <p className="text-sm text-red-700" data-testid="screenshot-error">
            {error}
          </p>
        )}
        {meta && (
          <figure>
            <img
              data-testid="screenshot-image"
              src={meta.bytes_url}
              crossOrigin="use-credentials"
              alt=""
              className="max-h-[70vh] w-full object-contain"
            />
            <figcaption className="mt-2 text-sm text-muted-foreground">
              {meta.width}×{meta.height} ·{' '}
              {Math.round(meta.size_bytes / 1024)} KB · {meta.captured_at}
            </figcaption>
          </figure>
        )}
      </div>
    </div>
  );
}

export default ScreenshotViewerModal;
